import { useEffect } from "react";
import { PAGE_SEO, type PageSeoPath } from "@/data/pageSeo";

type SeoOptions = {
  title: string;
  description: string;
  path: string; // e.g. "/about"
  ogTitle?: string;
  ogDescription?: string;
  noindex?: boolean;
};

const BASE = "https://chleb.ai";

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const usePageSeo = ({ title, description, path, ogTitle, ogDescription, noindex }: SeoOptions) => {
  useEffect(() => {
    const url = `${BASE}${path}`;
    document.head.querySelectorAll<HTMLScriptElement>("script[data-chleb-route-schema]").forEach((script) => {
      if (script.dataset.chlebRouteSchema !== path) script.remove();
    });
    document.title = title;
    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertCanonical(url);
    upsertMeta('meta[property="og:title"]', "property", "og:title", ogTitle ?? title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", ogDescription ?? description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", ogTitle ?? title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", ogDescription ?? description);
    upsertMeta('meta[name="robots"]', "name", "robots", noindex ? "noindex, follow" : "index, follow");
  }, [title, description, path, ogTitle, ogDescription, noindex]);
};

/**
 * The same hook, reading the route's title, description and noindex flag from
 * src/data/pageSeo.ts instead of a second hand-written copy. scripts/prerender.cjs
 * reads that module too, so the head a crawler sees and the head React installs
 * are the same strings by construction.
 */
export const usePageSeoFor = (path: PageSeoPath) => {
  const page = PAGE_SEO[path] as { title: string; description: string; noindex?: boolean };
  usePageSeo({
    title: page.title,
    description: page.description,
    path,
    noindex: page.noindex,
  });
};
